import { TeamCrest } from '@/components/TeamCrest'
import { cn } from '@/lib/utils'
import {
  KIND_LABEL, OPENNESS_TEXT, formatSignPct, kindOf, normalize, openness, rankSigns,
} from '@/lib/quiniela'
import { SIGNS, type QuinielaMatchView, type Sign } from '@/types/quiniela'

/** Color por tipo de apuesta (tokens existentes de la Mini App): simple = verde de marca, doble = cian de
 * precisión, triple = violeta IA. Doble y triple van rellenos y con acento lateral para reconocerse al instante. */
export const KIND_STYLE = {
  simple: {
    cell: 'border-brand/70 bg-brand/15 text-brand',
    chip: 'text-brand border-brand/40 bg-brand-soft/40',
    accent: '', row: '',
  },
  doble: {
    cell: 'bg-cyan text-black border-cyan',
    chip: 'text-cyan border-cyan/50 bg-cyan/10',
    accent: 'bg-cyan', row: 'bg-cyan/[0.05]',
  },
  triple: {
    cell: 'bg-violet text-black border-violet',
    chip: 'text-violet border-violet/50 bg-violet/10',
    accent: 'bg-violet', row: 'bg-violet/[0.06]',
  },
} as const

function ProbBar({ p, selected }: { p: Record<Sign, number>; selected?: Sign[] }) {
  const top = rankSigns(p)[0]
  return (
    <div className="flex h-[3px] w-full overflow-hidden rounded-full bg-border/60" aria-hidden>
      {SIGNS.map((s) => (
        <span
          key={s}
          style={{ width: `${p[s] * 100}%` }}
          className={cn(
            'h-full border-r border-black last:border-r-0',
            selected ? (selected.includes(s) ? 'bg-text-muted' : 'bg-border') : s === top ? 'bg-brand' : 'bg-text-dim/45',
          )}
        />
      ))}
    </div>
  )
}

/** Fila compacta de un partido (1-14). Jerarquía: número → equipos → signos 1/X/2 → probabilidad/contexto.
 * El signo favorito de JA se rellena en verde sin ocultar las otras probabilidades. `selected` marca el boleto
 * (simple / doble / triple); `onToggle` convierte las celdas en botones (Créala tú mismo). */
export function MatchProbRow({
  match, selected, onToggle,
}: {
  match: QuinielaMatchView
  selected?: Sign[]
  onToggle?: (sign: Sign) => void
  /** Se conserva por compatibilidad: la fila ya es compacta en todos los usos. */
  compact?: boolean
}) {
  const p = match.probabilities ? normalize(match.probabilities) : null
  const open = p ? openness(p) : null
  const kind = selected && selected.length ? kindOf(selected.length) : null
  const top = p ? rankSigns(p)[0] : null
  const special = kind === 'doble' || kind === 'triple'

  return (
    <div
      className={cn(
        'relative -mx-3 px-3 py-1.5 border-b border-border/50 last:border-b-0',
        special && kind && KIND_STYLE[kind].row,
        match.frozen && 'opacity-80',
      )}
    >
      {special && kind && <span className={cn('absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full', KIND_STYLE[kind].accent)} aria-hidden />}

      <div className="grid grid-cols-[1.1rem_minmax(0,1fr)_auto] items-center gap-x-2">
        <span className="figure text-[11px] font-bold text-text-dim text-right">{match.position}</span>

        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0 h-5">
            <TeamCrest src={match.home_logo} alt={match.home_name} size={16} />
            <span className="truncate text-[13px] font-bold text-text">{match.home_name}</span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0 h-5">
            <TeamCrest src={match.away_logo} alt={match.away_name} size={16} />
            <span className="truncate text-[12px] font-medium text-text-muted">{match.away_name}</span>
          </div>
        </div>

        <div className="flex items-center gap-1" role={onToggle ? 'group' : undefined} aria-label={`Partido ${match.position}`}>
          {SIGNS.map((s) => {
            const value = p ? p[s] : null
            const isSel = selected?.includes(s) ?? false
            const isTop = !selected && top === s
            const filled = isSel && kind ? KIND_STYLE[kind].cell : null
            const base = cn(
              'flex flex-col items-center justify-center rounded-lg border w-[2.6rem] transition-colors',
              onToggle ? 'h-10' : 'h-9',
              filled ? filled
                : isTop ? 'border-brand bg-brand/20 text-brand shadow-[0_0_10px_rgba(167,237,32,0.12)]'
                : 'border-border bg-panel-elevated text-text-muted',
              selected && selected.length > 0 && !isSel && 'opacity-60',
            )
            const inner = (
              <>
                <span className={cn('text-[9px] font-bold leading-none', isSel || isTop ? 'opacity-80' : 'text-text-dim')}>{s}</span>
                <span className={cn('figure leading-none mt-0.5', isTop ? 'text-[13px] font-black' : 'text-[12px] font-bold')}>
                  {value != null ? formatSignPct(value) : '—'}
                </span>
              </>
            )
            return onToggle ? (
              <button
                key={s} type="button" aria-pressed={isSel}
                aria-label={`Partido ${match.position}, signo ${s}`}
                onClick={() => onToggle(s)}
                className={cn(base, 'active:scale-95', !isSel && 'hover:border-text-dim/60')}
              >
                {inner}
              </button>
            ) : (
              <div key={s} className={base}>{inner}</div>
            )
          })}
        </div>
      </div>

      <div className="mt-1 grid grid-cols-[1.1rem_minmax(0,1fr)] gap-x-2 items-center">
        <span />
        {p ? (
          <div className="flex items-center gap-2.5">
            <div className="flex-1 min-w-0"><ProbBar p={p} selected={selected && selected.length ? selected : undefined} /></div>
            {kind ? (
              <>
                {special && (
                  <span className={cn('shrink-0 text-[10px] font-semibold leading-4', open === 'very_open' ? 'text-violet' : open === 'open' ? 'text-cyan' : 'text-text-dim')}>
                    {OPENNESS_TEXT[open!]}
                  </span>
                )}
                <span className={cn('shrink-0 rounded-full border px-2 text-[10px] font-bold leading-[15px]', KIND_STYLE[kind].chip)}>
                  {KIND_LABEL[kind]} · {selected!.join('')}
                </span>
              </>
            ) : open === 'clear' && !match.frozen ? null : (
              <span
                className={cn(
                  'shrink-0 text-[10px] font-semibold leading-3',
                  open === 'very_open' ? 'text-violet' : open === 'open' ? 'text-cyan' : 'text-text-dim',
                )}
              >
                {OPENNESS_TEXT[open!]}{match.frozen && ' · ya empezó'}
              </span>
            )}
          </div>
        ) : (
          <p className="text-[10px] text-warn">
            {match.frozen ? 'Ya empezó: sin cálculo previo.' : 'JA aún no tiene probabilidades de este partido.'}
          </p>
        )}
      </div>
    </div>
  )
}
